// LDP (landing-page) Store-copy tab — a form of blanks, not a blob.
//
// Evolves the old free-text StorePanel per docs/ldp-generation-handover.md: a shared level TEMPLATE with
// {{slots}} + a per-product FIELDS record, grouped into three sections (A Derived / B Proposed / C
// Localized). Everything AUTOSAVES to the backend as you type (debounced) and every save is a recoverable
// history version — your writing never lives only in this tab. Right rail: live preview + validation +
// Publish (gated so a page that fails validation can't go live).
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  getLdpMeta, getLdpTemplate, getLdpFields, saveLdpFields, getLdpHistory, restoreLdpFields,
  getLdpReferences, renderLdp, publishLdp,
  type LdpMeta, type LdpRenderResult, type LdpHistoryEntry,
} from '../lib/audio-course-client';
import LdpDesignedEditor from './LdpDesignedEditor';

type Fields = Record<string, string>;
type Reviewed = Record<string, boolean>;

const SECTION_LABEL: Record<'A' | 'B' | 'C', { title: string; blurb: string }> = {
  A: { title: 'Derived', blurb: 'System-filled from the product. Edit only to override.' },
  B: { title: 'Proposed', blurb: 'Drafted from the course — review and approve each.' },
  C: { title: 'Localized blanks', blurb: 'Only you write these. They carry the argument no competitor can copy.' },
};
const lsKey = (lang: string, level: string) => `ldp_draft_${lang}_${level}`;

// A readable mock-up of the page — renders Michael's markup the way the store roughly will:
// **bold**, ==highlight==, **==branded==** (bold + highlight), plus headings and bullet lists.
// This is an APPROXIMATION for reading in the admin; Blitzscale is the exact render (use Copy all).
function renderMockHtml(md: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s: string) => esc(s)
    .replace(/\*\*==(.+?)==\*\*/g, '<strong style="background:#fde68a;color:#1f2937;border-radius:3px;padding:0 3px">$1</strong>')
    .replace(/==(.+?)==/g, '<mark style="background:#fde68a;color:#1f2937;border-radius:3px;padding:0 3px">$1</mark>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
  const out: string[] = []; let inList = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  for (const raw of md.split('\n')) {
    const line = raw.replace(/\s+$/, '');
    if (/^### /.test(line)) { closeList(); out.push(`<h3 class="text-sm font-semibold text-zinc-100 mt-3">${inline(line.slice(4))}</h3>`); }
    else if (/^## /.test(line)) { closeList(); out.push(`<h2 class="text-base font-bold text-teal-300 mt-4">${inline(line.slice(3))}</h2>`); }
    else if (/^# /.test(line)) { closeList(); out.push(`<h1 class="text-lg font-bold text-zinc-100 mt-2">${inline(line.slice(2))}</h1>`); }
    else if (/^[-*] /.test(line)) { if (!inList) { out.push('<ul class="list-disc list-inside my-1 space-y-0.5">'); inList = true; } out.push(`<li>${inline(line.slice(2))}</li>`); }
    else if (line.trim() === '') { closeList(); }
    else { closeList(); out.push(`<p class="my-1.5 leading-relaxed">${inline(line)}</p>`); }
  }
  closeList();
  return out.join('\n');
}

const TA = 'w-full rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-zinc-200 focus:border-teal-500 focus:outline-none';
const LAB = 'text-xs font-medium text-zinc-300';

// STABLE module-level component. Defining this inside the panel made React rebuild it every render, which
// unmounted/remounted the textarea on every keystroke — stealing focus and jerking the page to the top.
// Kept at module scope with all state passed via props, the textarea stays mounted and focus is preserved.
function FieldEditor(p: {
  slot: string; small?: boolean; value: string; guide?: string; optional: boolean; required: boolean;
  isB: boolean; isC: boolean; reviewed: boolean; onChange: (v: string) => void; onToggleReviewed: () => void;
  refs: { CMN_TW: Fields | null; RUS: Fields | null }; openRef: boolean; onToggleRef: () => void;
}) {
  const rows = p.value.length > 400 ? 12 : p.value.length > 120 ? 7 : 3;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label className={LAB}>{p.slot}{p.optional && <span className="text-zinc-500 font-normal"> · optional</span>}{p.required && <span className="text-red-400 font-normal"> · required</span>}</label>
        {p.isB && (
          <label className="flex items-center gap-1 text-[11px] text-zinc-400">
            <input type="checkbox" checked={p.reviewed} onChange={p.onToggleReviewed} /> reviewed
          </label>
        )}
      </div>
      {p.guide && <p className="text-[11px] text-zinc-500 leading-snug">{p.guide}</p>}
      {p.small
        ? <input value={p.value} onChange={(e) => p.onChange(e.target.value)} className={TA} />
        : <textarea value={p.value} onChange={(e) => p.onChange(e.target.value)} rows={rows} className={TA} />}
      {p.isC && (p.refs.CMN_TW || p.refs.RUS) && (
        <div className="text-[11px]">
          <button onClick={p.onToggleRef} className="text-teal-400 hover:underline">
            {p.openRef ? 'hide examples' : 'see how Chinese / Russian filled this'}
          </button>
          {p.openRef && (
            <div className="mt-1 space-y-1 border-l-2 border-zinc-700 pl-2">
              {(['CMN_TW', 'RUS'] as const).map((r) => p.refs[r] && p.refs[r]![p.slot] != null && (
                <div key={r}><span className="text-zinc-500">{r === 'CMN_TW' ? 'Chinese' : 'Russian'}:</span> <span className="text-zinc-400 whitespace-pre-wrap">{p.refs[r]![p.slot]}</span></div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── In-context editor ────────────────────────────────────────────────────────────────────────────
// The whole landing page, with the FIXED template prose shown and each editable blank sitting inline
// exactly where it lands. This is what makes the copy editable "in context": you see the sentence a blank
// belongs to, the story a paragraph follows, and how your edit reads in place — instead of a flat list of
// blanks stripped of the words around them.

type Part = { t: 'text'; md: string } | { t: 'slot'; name: string };
type Row =
  | { kind: 'blank' }
  | { kind: 'dropdowns' }
  | { kind: 'block'; tag: 'h1' | 'h2' | 'h3' | 'p' | 'li'; parts: Part[] };

// Inline markup → HTML (same rules as the mock-up): **bold**, ==highlight==, **==branded==**, *italic*.
function inlineHtml(s: string): string {
  const esc = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc
    .replace(/\*\*==(.+?)==\*\*/g, '<strong style="background:#fde68a;color:#1f2937;border-radius:3px;padding:0 3px">$1</strong>')
    .replace(/==(.+?)==/g, '<mark style="background:#fde68a;color:#1f2937;border-radius:3px;padding:0 3px">$1</mark>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function splitParts(s: string): Part[] {
  const parts: Part[] = [];
  const re = /\{\{(\w+)\}\}/g;
  let last = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    const whole = m[0] ?? '';
    if (m.index > last) parts.push({ t: 'text', md: s.slice(last, m.index) });
    parts.push({ t: 'slot', name: m[1] ?? '' });
    last = m.index + whole.length;
  }
  if (last < s.length) parts.push({ t: 'text', md: s.slice(last) });
  return parts;
}

function parseTemplate(body: string): Row[] {
  const rows: Row[] = [];
  for (const raw of (body || '').split('\n')) {
    const line = raw.replace(/\s+$/, '');
    if (line.trim() === '') { rows.push({ kind: 'blank' }); continue; }
    if (line.trim() === '{{dropdowns}}') { rows.push({ kind: 'dropdowns' }); continue; }
    let tag: 'h1' | 'h2' | 'h3' | 'p' | 'li' = 'p'; let content = line;
    if (/^### /.test(line)) { tag = 'h3'; content = line.slice(4); }
    else if (/^## /.test(line)) { tag = 'h2'; content = line.slice(3); }
    else if (/^# /.test(line)) { tag = 'h1'; content = line.slice(2); }
    else if (/^[-*] /.test(line)) { tag = 'li'; content = line.slice(2); }
    rows.push({ kind: 'block', tag, parts: splitParts(content) });
  }
  return rows;
}

// Slots that always render as a full-width block editor (they occupy their own paragraph/scene). Everything
// else editable (headline_obstacle, accent_phrase, dropdown titles) renders inline in the sentence flow.
const BLOCK_EDIT = new Set([
  'pain_scene', 'situations', 'lie_1', 'lie_2', 'lie_3', 'marketplace_extra', 'credential_language',
  'benefit_extra', 'proof_block', 'ps_scene', 'bonuses', 'social_proof', 'audio_samples', 'book_pages',
]);
const HDR: Record<string, string> = {
  h1: 'text-lg font-bold text-zinc-100 mt-2', h2: 'text-base font-bold text-teal-300 mt-4',
  h3: 'text-sm font-semibold text-zinc-100 mt-3', p: 'my-1.5 leading-relaxed', li: '',
};

// A short blank sitting in the flow of a sentence (e.g. accent_phrase).
function InlineSlot(p: { name: string; value: string; onChange: (v: string) => void; guide?: string }) {
  const w = Math.max(6, Math.min(46, (p.value || p.name).length + 1));
  return (
    <input value={p.value} onChange={(e) => p.onChange(e.target.value)} title={p.guide || p.name} placeholder={p.name} size={w}
      className="inline bg-teal-950/40 border-b border-teal-600 text-teal-100 px-1 rounded-sm focus:outline-none focus:border-teal-400" />
  );
}

// A block blank (paragraph / list / scene) shown where it lands, labelled, with guidance + an optional example.
function BlockSlot(p: {
  name: string; value: string; onChange: (v: string) => void; guide?: string; cls: 'A' | 'B' | 'C' | null; optional: boolean;
  refs?: { CMN_TW: Fields | null; RUS: Fields | null }; openRef?: boolean; onToggleRef?: () => void;
}) {
  const rows = p.value.length > 400 ? 12 : p.value.length > 120 ? 7 : 3;
  const empty = !p.value.trim();
  return (
    <div className={`my-2 rounded border ${empty && !p.optional ? 'border-amber-700/60' : 'border-teal-800/50'} bg-zinc-900/60 p-2 space-y-1`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-teal-300">{p.name}{p.optional && <span className="text-zinc-500 font-normal"> · optional</span>}{!p.optional && empty && <span className="text-amber-400 font-normal"> · needs text</span>}</span>
        {p.cls === 'C' && p.refs && (p.refs.CMN_TW || p.refs.RUS) && p.onToggleRef && (
          <button onClick={p.onToggleRef} className="text-[11px] text-teal-400 hover:underline">{p.openRef ? 'hide example' : 'example'}</button>
        )}
      </div>
      {p.guide && <p className="text-[11px] text-zinc-500 leading-snug">{p.guide}</p>}
      <textarea value={p.value} onChange={(e) => p.onChange(e.target.value)} rows={rows} className={TA} />
      {p.openRef && p.refs && (
        <div className="text-[11px] space-y-1 border-l-2 border-zinc-700 pl-2">
          {(['CMN_TW', 'RUS'] as const).map((r) => p.refs![r] && p.refs![r]![p.name] != null && (
            <div key={r}><span className="text-zinc-500">{r === 'CMN_TW' ? 'Chinese' : 'Russian'}:</span> <span className="text-zinc-400 whitespace-pre-wrap">{p.refs![r]![p.name]}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function InContextEditor(p: {
  rows: Row[]; fields: Fields; meta: LdpMeta; onChange: (slot: string, v: string) => void;
  refs: { CMN_TW: Fields | null; RUS: Fields | null }; openRefs: Record<string, boolean>; onToggleRef: (s: string) => void;
}) {
  const injected = new Set(p.meta.injected);
  const A = new Set(p.meta.classes.A), B = new Set(p.meta.classes.B), C = new Set(p.meta.classes.C);
  const optional = new Set(p.meta.optional);
  const clsOf = (n: string): 'A' | 'B' | 'C' | null => (A.has(n) ? 'A' : B.has(n) ? 'B' : C.has(n) ? 'C' : null);
  const isEdit = (n: string) => B.has(n) || C.has(n) || n === 'audio_samples' || n === 'book_pages';
  const isDerived = (n: string) => A.has(n) && n !== 'audio_samples' && n !== 'book_pages';
  const blockSlot = (n: string) => (
    <BlockSlot key={n} name={n} value={p.fields[n] || ''} onChange={(v) => p.onChange(n, v)} guide={p.meta.guidance[n]}
      cls={clsOf(n)} optional={optional.has(n)} refs={p.refs} openRef={!!p.openRefs[n]} onToggleRef={() => p.onToggleRef(n)} />
  );

  const renderRow = (row: Row, ri: number): ReactNode[] => {
    if (row.kind === 'blank') return [<div key={`b${ri}`} className="h-1.5" />];
    if (row.kind === 'dropdowns') {
      const dds: ReactNode[] = [];
      for (let i = 1; i <= 4; i++) {
        const t = `dropdown_${i}_title`, b = `dropdown_${i}_body`;
        dds.push(
          <div key={`dd${i}`} className="my-2 rounded border border-zinc-800 bg-zinc-900/40 p-2 space-y-1">
            <div className="text-[11px] text-zinc-500">Dropdown {i} — an expandable “why” section (leave both blank to hide it)</div>
            <BlockSlot name={t} value={p.fields[t] || ''} onChange={(v) => p.onChange(t, v)} guide={p.meta.guidance[t]} cls="C" optional />
            <BlockSlot name={b} value={p.fields[b] || ''} onChange={(v) => p.onChange(b, v)} guide={p.meta.guidance[b]} cls="C" optional
              refs={p.refs} openRef={!!p.openRefs[b]} onToggleRef={() => p.onToggleRef(b)} />
          </div>,
        );
      }
      return [<div key={`dds${ri}`}>{dds}</div>];
    }
    const nodes: ReactNode[] = [];
    let buf: ReactNode[] = [];
    let bi = 0;
    const Tag = (row.tag === 'li' ? 'li' : row.tag) as keyof JSX.IntrinsicElements;
    const flush = () => {
      if (!buf.length) return;
      const b = buf; buf = [];
      if (row.tag === 'li') nodes.push(<ul key={`u${ri}_${bi}`} className="list-disc list-inside my-1"><li>{b}</li></ul>);
      else nodes.push(<Tag key={`t${ri}_${bi}`} className={HDR[row.tag] || ''}>{b}</Tag>);
      bi++;
    };
    row.parts.forEach((part, pi) => {
      if (part.t === 'text') { buf.push(<span key={`x${ri}_${pi}`} dangerouslySetInnerHTML={{ __html: inlineHtml(part.md) }} />); return; }
      const n = part.name;
      if (n === 'dropdowns') return;
      if (injected.has(n)) { buf.push(<span key={`i${ri}_${pi}`} title="the store fills this in at checkout" className="mx-0.5 px-1 rounded bg-zinc-800 text-zinc-400 text-xs align-baseline">{n}</span>); return; }
      if (isEdit(n) && BLOCK_EDIT.has(n)) { flush(); nodes.push(blockSlot(n)); return; }
      if (isEdit(n)) { buf.push(<InlineSlot key={`e${ri}_${pi}`} name={n} value={p.fields[n] || ''} onChange={(v) => p.onChange(n, v)} guide={p.meta.guidance[n]} />); return; }
      if (isDerived(n)) { buf.push(<span key={`d${ri}_${pi}`} className="text-zinc-400" title="filled automatically from the product">{(p.fields[n] || '').trim() || `{{${n}}}`}</span>); return; }
      buf.push(<span key={`z${ri}_${pi}`}>{`{{${n}}}`}</span>);
    });
    flush();
    return nodes;
  };

  return <div className="max-w-3xl text-sm text-zinc-200">{p.rows.flatMap((row, ri) => renderRow(row, ri))}</div>;
}

export default function LdpStorePanel({ langs, levels }: { langs: Array<{ code: string; name: string }>; levels: string[] }) {
  const [lang, setLang] = useState('CMN_TW');
  const [level, setLevel] = useState('A1');

  const [meta, setMeta] = useState<LdpMeta | null>(null);
  const [refs, setRefs] = useState<{ CMN_TW: Fields | null; RUS: Fields | null }>({ CMN_TW: null, RUS: null });
  const [slots, setSlots] = useState<string[]>([]);
  const [templateBody, setTemplateBody] = useState('');
  const [editMode, setEditMode] = useState<'designed' | 'context' | 'fields'>('designed');
  const [fields, setFields] = useState<Fields>({});
  const [prose, setProse] = useState<Record<string, string>>({});
  const [reviewed, setReviewed] = useState<Reviewed>({});
  const [rev, setRev] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [preview, setPreview] = useState<LdpRenderResult | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [history, setHistory] = useState<LdpHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pub, setPub] = useState<{ ok?: boolean; blocked?: boolean; store_url?: string; detail?: string } | null>(null);
  const [openRefs, setOpenRefs] = useState<Record<string, boolean>>({});
  const [previewMode, setPreviewMode] = useState<'rendered' | 'markdown'>('rendered');
  const [copied, setCopied] = useState(false);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // one-time: slot classes/guidance + reference examples
  useEffect(() => {
    getLdpMeta().then(setMeta).catch(() => {});
    getLdpReferences().then((r) => setRefs(r.references)).catch(() => {});
  }, []);

  const refresh = useCallback((lg: string, lv: string) => {
    renderLdp(lg, lv).then(setPreview).catch(() => {});
    getLdpHistory(lg, lv).then((r) => setHistory(r.history)).catch(() => {});
  }, []);

  const load = useCallback(async (lg: string, lv: string) => {
    setLoading(true); setErr(null); setPub(null);
    try {
      const [tpl, f] = await Promise.all([getLdpTemplate(lv), getLdpFields(lg, lv)]);
      setSlots(f.slots.length ? f.slots : tpl.slots);
      setTemplateBody(tpl.body || '');
      // Prefer the server draft; if a newer local backup exists (e.g. a crash before the last autosave
      // flushed), keep whichever the user last touched. Server is source of truth on rev.
      let nextFields = f.fields || {};
      let nextProse = f.prose || {};
      try {
        const raw = localStorage.getItem(lsKey(lg, lv));
        if (raw) { const bak = JSON.parse(raw); if (bak && bak.rev >= (f.rev || 0) && bak.fields) { nextFields = bak.fields; if (bak.prose) nextProse = bak.prose; } }
      } catch { /* localStorage may be blocked */ }
      setFields(nextFields);
      setProse(nextProse);
      setReviewed(f.reviewed || {});
      setRev(f.rev || 0);
      setSavedAt(f.updatedAt);
      setSaveState('idle');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Load failed'); }
    finally { setLoading(false); }
    refresh(lg, lv);
  }, [refresh]);

  useEffect(() => { load(lang, level); }, [lang, level, load]);

  // Autosave: persist on every (debounced) edit, back up to localStorage immediately, refresh preview.
  const scheduleSave = useCallback((nextFields: Fields, nextReviewed: Reviewed, nextProse: Record<string, string>) => {
    try { localStorage.setItem(lsKey(lang, level), JSON.stringify({ rev: rev + 1, fields: nextFields, prose: nextProse, savedAt: new Date().toISOString() })); } catch { /* ignore */ }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await saveLdpFields(lang, level, nextFields, nextReviewed, nextProse);
        setRev(r.rev); setSavedAt(r.savedAt); setSaveState('saved');
        refresh(lang, level);
      } catch { setSaveState('error'); }
    }, 1200);
  }, [lang, level, rev, refresh]);

  const setField = (slot: string, val: string) => {
    setFields((f) => { const n = { ...f, [slot]: val }; scheduleSave(n, reviewed, prose); return n; });
  };
  const setProseLine = (lineIdx: number, val: string) => {
    setProse((pr) => { const n = { ...pr, [String(lineIdx)]: val }; scheduleSave(fields, reviewed, n); return n; });
  };
  const toggleReviewed = (slot: string) => {
    setReviewed((rv) => { const n = { ...rv, [slot]: !rv[slot] }; scheduleSave(fields, n, prose); return n; });
  };

  const doPublish = async () => {
    setPublishing(true); setPub(null);
    try {
      const r = await publishLdp(lang, level);
      if (r.blocked) setPub({ blocked: true });
      else if (r.ok) setPub({ ok: true, store_url: r.store_url });
      else setPub({ detail: r.detail || 'publish failed' });
    } catch (e) { setPub({ detail: e instanceof Error ? e.message : 'publish failed' }); }
    finally { setPublishing(false); refresh(lang, level); }
  };

  const doRestore = async (h: LdpHistoryEntry) => {
    await restoreLdpFields(lang, level, h.savedAt, h.rev).catch(() => {});
    load(lang, level);
  };

  // Copy the WHOLE rendered page (markup intact) so it can be pasted into Blitzscale to render exactly.
  const copyAll = async () => {
    if (!preview?.markdown) return;
    try { await navigator.clipboard.writeText(preview.markdown); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { /* clipboard blocked — the markdown is still visible/selectable in the preview */ }
  };

  const isDropdown = (s: string) => s.startsWith('dropdown_');
  const classSlots = useMemo(() => {
    const inC = new Set(slots);
    const pick = (cls: 'A' | 'B' | 'C') => (meta ? meta.classes[cls].filter((s) => inC.has(s)) : []);
    return { A: pick('A'), B: pick('B'), C: pick('C') };
  }, [meta, slots]);

  const rows = useMemo(() => parseTemplate(templateBody), [templateBody]);
  const optional = useMemo(() => new Set(meta?.optional || []), [meta]);
  const blocking = preview?.blocking || [];
  const warnings = preview?.warnings || [];
  const comp = preview?.completeness;

  // Props for the stable module-level FieldEditor. Recomputed per render (cheap) but the COMPONENT type is
  // stable, so the textarea is never remounted — no focus loss, no scroll jump.
  const feProps = (s: string) => ({
    value: fields[s] || '',
    guide: meta?.guidance[s],
    optional: optional.has(s),
    required: !(fields[s] || '').trim() && !optional.has(s) && (classSlots.A.includes(s) || classSlots.C.includes(s)),
    isB: classSlots.B.includes(s),
    isC: classSlots.C.includes(s),
    reviewed: !!reviewed[s],
    onChange: (v: string) => setField(s, v),
    onToggleReviewed: () => toggleReviewed(s),
    refs,
    openRef: !!openRefs[s],
    onToggleRef: () => setOpenRefs((o) => ({ ...o, [s]: !o[s] })),
  });

  const saveBadge = {
    idle: <span className="text-zinc-500">Up to date</span>,
    saving: <span className="text-amber-400">Saving…</span>,
    saved: <span className="text-teal-400">Saved · rev {rev}{savedAt ? ` · ${new Date(savedAt).toLocaleTimeString()}` : ''}</span>,
    error: <span className="text-red-400">Save failed — your text is backed up locally; retrying on next edit</span>,
  }[saveState];

  return (
    <div className="space-y-4">
      {/* selectors + save status */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-sm text-zinc-200">
            {langs.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
          </select>
          <select value={level} onChange={(e) => setLevel(e.target.value)} className="rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-sm text-zinc-200">
            {levels.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
          </select>
          <span className="text-xs">{saveBadge}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={copyAll} title="Copy the whole landing page (markup intact) to paste into Blitzscale" className="px-3 py-1.5 rounded bg-violet-800 text-violet-100 text-sm hover:bg-violet-700">{copied ? '✓ Copied' : 'Copy all'}</button>
          <button onClick={() => setShowPreview((v) => !v)} className="px-3 py-1.5 rounded bg-zinc-800 text-zinc-300 text-sm hover:text-zinc-100">{showPreview ? 'Hide preview' : 'Preview'}</button>
          <button onClick={() => setShowHistory((v) => !v)} className="px-3 py-1.5 rounded bg-zinc-800 text-zinc-300 text-sm hover:text-zinc-100">History ({history.length})</button>
          <button onClick={doPublish} disabled={publishing || blocking.length > 0} title={blocking.length ? 'Fix the blocking items first' : 'Publish to the store'}
            className="px-3 py-1.5 rounded bg-teal-800 text-teal-100 text-sm hover:bg-teal-700 disabled:opacity-50">{publishing ? 'Publishing…' : 'Publish'}</button>
        </div>
      </div>

      {err && <p className="text-sm text-red-400">Load failed: {err}</p>}
      {loading ? <p className="text-sm text-zinc-500">Loading…</p> : (
        <>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* form */}
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1 bg-zinc-800 rounded p-0.5 text-xs">
                <button onClick={() => setEditMode('designed')} className={`px-2.5 py-1 rounded ${editMode === 'designed' ? 'bg-zinc-700 text-teal-300' : 'text-zinc-400 hover:text-zinc-200'}`}>Designed page</button>
                <button onClick={() => setEditMode('context')} className={`px-2.5 py-1 rounded ${editMode === 'context' ? 'bg-zinc-700 text-teal-300' : 'text-zinc-400 hover:text-zinc-200'}`}>In context</button>
                <button onClick={() => setEditMode('fields')} className={`px-2.5 py-1 rounded ${editMode === 'fields' ? 'bg-zinc-700 text-teal-300' : 'text-zinc-400 hover:text-zinc-200'}`}>By field</button>
              </div>
              <span className="text-[11px] text-zinc-500">{editMode === 'designed' ? 'The real landing page — click any line to edit it. Store fills the price chips at checkout.' : editMode === 'context' ? 'The whole page — edit each blank where it lands. Grey = fixed or auto-filled text.' : 'Every blank grouped by who fills it.'}</span>
            </div>
            {editMode === 'designed' && meta ? (
              <LdpDesignedEditor body={templateBody} fields={fields} prose={prose} meta={meta} lang={lang} level={level} onField={setField} onProse={setProseLine} />
            ) : editMode === 'context' && meta ? (
              <InContextEditor rows={rows} fields={fields} meta={meta} onChange={setField} refs={refs} openRefs={openRefs} onToggleRef={(s) => setOpenRefs((o) => ({ ...o, [s]: !o[s] }))} />
            ) : (
            <div className="space-y-5">
            {(['A', 'B', 'C'] as const).map((cls) => {
              const list = classSlots[cls].filter((s) => !isDropdown(s));
              const dds = cls === 'C' ? classSlots.C.filter(isDropdown) : [];
              if (!list.length && !dds.length) return null;
              return (
                <section key={cls} className="rounded-lg border border-zinc-800 p-3 space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-200">{SECTION_LABEL[cls].title}</h3>
                    <p className="text-[11px] text-zinc-500">{SECTION_LABEL[cls].blurb}</p>
                  </div>
                  {list.map((s) => <FieldEditor key={s} slot={s} small={cls === 'A'} {...feProps(s)} />)}
                  {dds.length > 0 && (
                    <div className="space-y-3">
                      <p className={LAB}>Dropdowns (optional, shown in order)</p>
                      {[1, 2, 3, 4].map((i) => {
                        const t = `dropdown_${i}_title`, b = `dropdown_${i}_body`;
                        if (!classSlots.C.includes(t)) return null;
                        return (
                          <div key={i} className="rounded border border-zinc-800 p-2 space-y-2">
                            <FieldEditor key={t} slot={t} small {...feProps(t)} />
                            <FieldEditor key={b} slot={b} {...feProps(b)} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
            </div>
            )}
          </div>

          {/* right rail: validation + preview + history */}
          <div className="lg:w-96 shrink-0 space-y-3">
            <div className="rounded-lg border border-zinc-800 p-3 space-y-2 lg:sticky lg:top-4">
              {comp && (
                <div className="text-xs text-zinc-400">Completeness: <b className={comp.filled === comp.required ? 'text-teal-400' : 'text-amber-400'}>{comp.filled}/{comp.required}</b> required filled</div>
              )}
              {blocking.length > 0 ? (
                <div className="text-xs">
                  <div className="text-red-400 font-medium mb-1">Blocks publish ({blocking.length}):</div>
                  <ul className="list-disc list-inside text-red-300 space-y-0.5">{blocking.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </div>
              ) : <div className="text-xs text-teal-400">No blocking issues — ready to publish.</div>}
              {warnings.length > 0 && (
                <div className="text-xs">
                  <div className="text-amber-400 font-medium mb-1">Warnings ({warnings.length}):</div>
                  <ul className="list-disc list-inside text-amber-300/80 space-y-0.5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
              {pub && (
                <div className="text-xs pt-1 border-t border-zinc-800">
                  {pub.ok && <span className="text-teal-400">Published.{pub.store_url && <> <a href={pub.store_url} target="_blank" rel="noreferrer" className="underline">View</a></>}</span>}
                  {pub.blocked && <span className="text-red-400">Not published — fix the blocking items above.</span>}
                  {pub.detail && <span className="text-red-400">Publish error: {pub.detail}</span>}
                </div>
              )}
            </div>

            {showHistory && (
              <div className="rounded-lg border border-zinc-800 p-3 space-y-1 max-h-72 overflow-y-auto">
                <div className={LAB}>Saved versions (newest first)</div>
                {history.length === 0 ? <p className="text-xs text-zinc-500">No saved versions yet.</p> : history.map((h) => (
                  <div key={`${h.savedAt}_${h.rev}`} className="flex items-center justify-between text-xs py-1 border-t border-zinc-800/60">
                    <span className="text-zinc-400">rev {h.rev} · {new Date(h.savedAt).toLocaleString()}</span>
                    <button onClick={() => doRestore(h)} className="px-2 py-0.5 rounded bg-zinc-800 text-teal-400 hover:bg-zinc-700">Restore</button>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
        {showPreview && preview && (
          <div className="rounded-lg border border-zinc-800 p-3 space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-zinc-200">Landing-page mock-up</span>
                <div className="flex gap-1 bg-zinc-800 rounded p-0.5 text-xs">
                  <button onClick={() => setPreviewMode('rendered')} className={`px-2 py-0.5 rounded ${previewMode === 'rendered' ? 'bg-zinc-700 text-teal-300' : 'text-zinc-400 hover:text-zinc-200'}`}>Rendered</button>
                  <button onClick={() => setPreviewMode('markdown')} className={`px-2 py-0.5 rounded ${previewMode === 'markdown' ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-400 hover:text-zinc-200'}`}>Markdown</button>
                </div>
              </div>
              <button onClick={copyAll} className="px-3 py-1 rounded bg-violet-800 text-violet-100 text-xs hover:bg-violet-700 w-fit">{copied ? '✓ Copied — paste into Blitzscale' : 'Copy all for Blitzscale'}</button>
            </div>
            <p className="text-[11px] text-zinc-500">Approximate mock-up for reading here. Blitzscale renders the exact page — use “Copy all” and paste it there.</p>
            {previewMode === 'rendered'
              ? <div className="max-w-3xl mx-auto bg-zinc-950 rounded p-4 text-sm text-zinc-200 max-h-[36rem] overflow-y-auto" dangerouslySetInnerHTML={{ __html: renderMockHtml(preview.markdown) }} />
              : <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap font-mono max-h-[36rem] overflow-y-auto bg-zinc-950 rounded p-3">{preview.markdown}</pre>}
          </div>
        )}
        </>
      )}
    </div>
  );
}
